type TRadioButtonProps = {
  name: string;
  value: string;
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label: string;
};

type TRadioOption = {
  value: string;
  label: string;
};

type TRadioButtonGroupProps = {
  name: string;
  label?: string;
  options: TRadioOption[];
  value: string;
  onChange: (value: string) => void;
  direction?: 'row' | 'column';
  gap?: string;
  formatLabel?: (label: string) => string;
  dataTestId?: string;
};

const RadioButton: React.FC<TRadioButtonProps & { dataTestId?: string }> = ({
  name,
  value,
  checked,
  onChange,
  label,
  dataTestId,
}) => (
  <label
    className="relative flex items-center cursor-pointer select-none text-lg w-fit"
    data-testid={dataTestId}
  >
    <input
      type="radio"
      name={name}
      value={value}
      checked={checked}
      onChange={onChange}
      className="absolute opacity-0 cursor-pointer"
    />
    <span
      className={`relative inline-block w-5 h-5 bg-white border rounded-full transition-colors duration-200 ease-in-out
        ${checked ? 'bg-brand-default! border-brand-default' : 'border-neutral-light-200'} hover:bg-neutral-light-50`}
    >
      <span
        className={`absolute w-2.5 h-2.5 bg-white rounded-full transition-opacity duration-200 ease-in-out
          ${checked ? 'opacity-100' : 'opacity-0'} left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2`}
      ></span>
    </span>
    <span className="ms-1.5 text-sm font-medium text-neutral-primary">
      {label}
    </span>
  </label>
);

export const RadioButtonGroup: React.FC<TRadioButtonGroupProps> = ({
  name,
  label,
  options,
  value,
  onChange,
  direction = 'row',
  gap = 'gap-6',
  formatLabel,
  dataTestId,
}) => (
  <div className="flex flex-col gap-4" data-testid={dataTestId}>
    {label && (
      <label className="block text-sm font-medium text-neutral-primary leading-5 capitalize">
        {label}
      </label>
    )}
    <div
      className={`flex ${direction === 'row' ? 'flex-row' : 'flex-col'} ${gap} items-center`}
    >
      {options.map((option) => (
        <RadioButton
          key={option.value}
          name={name}
          value={option.value}
          label={formatLabel ? formatLabel(option.label) : option.label}
          checked={value === option.value}
          onChange={(e) => onChange(e.target.value)}
          dataTestId={dataTestId ? `${dataTestId}-${option.value}` : undefined}
        />
      ))}
    </div>
  </div>
);
